import React, { ReactNode, useContext } from "react";

interface WizardWorkflowContextProviderProps<T> {
  context?: T;
  children: ReactNode[] | ReactNode;
}

const WizardWorkflowContext = React.createContext<unknown | undefined>(
  undefined
);

/**
 * React hook to get the workflow context of the Wizard.
 *
 * @export
 * @template T
 * @returns {T}
 */
export function useWizardWorkflowContext<T>(): T {
  const context = useContext(WizardWorkflowContext);

  if (!context) {
    throw new Error(
      "Wizard workflowContext cannot be undefined when using useWizardWorkflowContext hook in any step."
    );
  }

  return context as T;
}

/**
 * React component that wraps WizardWorkflowContext.
 *
 * @template T
 * @param {WizardWorkflowContextProviderProps<T>} {
 *   context,
 *   children,
 * }
 * @returns {JSX.Element}
 */
function WizardWorkflowContextProvider<T>({
  context,
  children,
}: WizardWorkflowContextProviderProps<T>): JSX.Element {
  return (
    <WizardWorkflowContext.Provider value={context}>
      {children}
    </WizardWorkflowContext.Provider>
  );
}

export default WizardWorkflowContextProvider;
